/**
 * DAWModalsContainer Component
 * Container for all DAW modals, panels, and floating windows
 */

import React from 'react';
import { QuickTour } from '../../QuickTour';
import { ProjectTemplateSelector } from '../../ProjectTemplates';
import { TrackPresetSelector } from '../plugins';
import {
  ChordWizardWrapper,
  SmartTempoPanelWrapper,
  QuickStartWrapper,
  AIMixingPanelWrapper,
  MasteringPanelWrapper,
  AIMasteringPanelWrapper,
  CollaborationPanelWrapper,
  JamSessionPanelWrapper,
  PluginRackPanel,
  PluginBrowserWrapper,
  PluginMarketplaceWrapper,
  ChainPresetSelectorWrapper,
  BatchExporterWrapper,
  SessionManagerWrapper,
  JamSessionModalWrapper,
  KeyboardShortcutsPanelWrapper,
  MarkersPanelWrapper,
  MarkerPickerWrapper,
  StatusBar,
  AdvancedEffectsModal,
  EffectPresetsBrowserWrapper,
  AIMixAssistantWrapper,
  AIChordGeneratorWrapper,
  AILyricAssistantWrapper,
  AIDrumGeneratorWrapper,
  VideoSyncPanelWrapper,
  SampleLibraryWrapper,
} from './index';
import { SAMPLE_RATE, TRACK_COLORS } from './index';

export const DAWModalsContainer = ({
  // Chord Wizard
  showChordWizard,
  setShowChordWizard,
  handleChordWizardInsert,
  handleChordWizardAddToChordTrack,
  audioContextRef,
  
  // Smart Tempo
  showSmartTempo,
  setShowSmartTempo,
  tracks,
  bpm,
  setBpm,
  
  // Quick Start
  showQuickStart,
  setShowQuickStart,
  handleQuickStartTemplate,
  
  // AI Mixing
  showAIMixing,
  setShowAIMixing,
  masterVolume,
  projectName,
  setMasterVolume,
  updateTrack,
  
  // Mastering
  showMastering,
  setShowMastering,
  exportWithMastering,
  isMasteringExporting,
  
  // AI Mastering
  showAIMastering,
  setShowAIMastering,
  projectId,
  audioCharacteristics,
  handleApplyMasteringSuggestion,
  
  // Collaboration
  showCollaboration,
  setShowCollaboration,
  collabSessionId,
  user,
  isPlaying,
  playAll,
  stopAll,
  setCurrentTime,
  setTimeSignature,
  setRemoteCursors,
  setCollabUsers,
  handleLeaveSession,
  
  // Jam Session Panel
  showJamSessionPanel,
  setShowJamSessionPanel,
  activeJamSession,
  setActiveJamSession,
  
  // Plugin Rack
  showPluginRack,
  setShowPluginRack,
  masterPluginChain,
  setShowPluginBrowser,
  setShowChainPresets,
  handleRemoveMasterPlugin,
  handleToggleMasterPluginBypass,
  handleReorderMasterPlugins,
  handleMasterPluginParamChange,
  handleDuplicateMasterPlugin,
  handleResetMasterPlugin,
  
  // Plugin Browser
  showPluginBrowser,
  handleAddMasterPlugin,
  trackPluginBrowserTarget,
  setTrackPluginBrowserTarget,
  handleAddTrackPlugin,
  
  // Plugin Marketplace
  showPluginMarketplace,
  setShowPluginMarketplace,
  
  // Chain Presets
  showChainPresets,
  handleLoadChainPreset,
  
  // Track Presets
  trackPresetTarget,
  setTrackPresetTarget,
  handleLoadTrackPreset,
  
  // Batch Exporter
  showBatchExporter,
  setShowBatchExporter,
  handleBatchImportTracks,
  
  // Session Manager
  showSessionManager,
  setShowSessionManager,
  handleCreateSession,
  handleJoinSession,
  
  // Jam Session Modal
  showJamSessionModal,
  setShowJamSessionModal,
  
  // Keyboard Shortcuts
  showKeyboardShortcuts,
  setShowKeyboardShortcuts,
  
  // Quick Tour
  showTour,
  closeTour,
  completeTour,
  
  // Template Selector
  showTemplateSelector,
  setShowTemplateSelector,
  handleSelectTemplate,
  
  // Markers Panel
  showMarkersPanel,
  markers,
  regions,
  addMarker,
  updateMarker,
  deleteMarker,
  addRegion,
  updateRegion,
  deleteRegion,
  playbackStartTimeRef,
  currentTime,
  totalDuration,
  
  // Marker Picker
  showMarkerPicker,
  setShowMarkerPicker,
  pendingMarkerTime,
  setPendingMarkerTime,
  markerPickerPosition,
  
  // Status Bar
  isSaving,
  
  // Advanced Effects
  showAdvancedEffects,
  setShowAdvancedEffects,
  selectedEffectsTrackId,
  advancedEffectsData,
  setAdvancedEffectsData,
  isPro,
  isStudio,
  setShowEffectPresetsBrowser,
  
  // Effect Presets Browser
  showEffectPresetsBrowser,
  
  // AI Mix Assistant
  showAIMixAssistant,
  setShowAIMixAssistant,
  setMasterPluginChain,
  
  // AI Chord Generator
  showAIChordGenerator,
  setShowAIChordGenerator,
  setTracks,
  
  // AI Lyric Assistant
  showAILyricAssistant,
  setShowAILyricAssistant,
  
  // AI Drum Generator
  showAIDrumGenerator,
  setShowAIDrumGenerator,
  
  // Video Sync
  showVideoSync,
  setShowVideoSync,
  
  // Sample Library
  showSampleLibrary,
  setShowSampleLibrary,
}) => {
  return (
    <>
      {/* Chord Wizard Modal */}
      <ChordWizardWrapper
        isOpen={showChordWizard}
        onClose={() => setShowChordWizard(false)}
        onInsertToTrack={handleChordWizardInsert}
        onAddToChordTrack={handleChordWizardAddToChordTrack}
        audioContextRef={audioContextRef}
      />
      
      {/* Smart Tempo Panel - Floating */}
      <SmartTempoPanelWrapper
        isOpen={showSmartTempo}
        onClose={() => setShowSmartTempo(false)}
        tracks={tracks}
        audioContextRef={audioContextRef}
        currentBpm={bpm}
        setBpm={setBpm}
      />
      
      {/* Quick Start Templates Modal */}
      <QuickStartWrapper
        isOpen={showQuickStart}
        onClose={() => setShowQuickStart(false)}
        onSelectTemplate={handleQuickStartTemplate}
      />
      
      {/* AI Mixing Assistant Panel - Right Side */}
      <AIMixingPanelWrapper
        isOpen={showAIMixing}
        onClose={() => setShowAIMixing(false)}
        tracks={tracks}
        masterVolume={masterVolume}
        bpm={bpm}
        projectName={projectName}
        setMasterVolume={setMasterVolume}
        updateTrack={updateTrack}
      />
      
      {/* Mastering Panel - Right Side */}
      <MasteringPanelWrapper
        isOpen={showMastering}
        onClose={() => setShowMastering(false)}
        tracks={tracks}
        audioContextRef={audioContextRef}
        exportWithMastering={exportWithMastering}
        isMasteringExporting={isMasteringExporting}
      />
      
      {/* AI Mastering Suggestions Panel - Right Side */}
      <AIMasteringPanelWrapper
        isOpen={showAIMastering}
        onClose={() => setShowAIMastering(false)}
        projectId={projectId}
        projectName={projectName}
        audioCharacteristics={audioCharacteristics}
        handleApplyMasteringSuggestion={handleApplyMasteringSuggestion}
      />
      
      {/* Live Collaboration Panel - Right Side */}
      <CollaborationPanelWrapper
        isOpen={showCollaboration}
        onClose={() => setShowCollaboration(false)}
        collabSessionId={collabSessionId}
        user={user}
        isPlaying={isPlaying}
        audioContextRef={audioContextRef}
        playAll={playAll}
        stopAll={stopAll}
        setCurrentTime={setCurrentTime}
        setBpm={setBpm}
        setTimeSignature={setTimeSignature}
        setRemoteCursors={setRemoteCursors}
        setCollabUsers={setCollabUsers}
        updateTrack={updateTrack}
        handleLeaveSession={handleLeaveSession}
      />
      
      {/* Jam Session Panel - Right Side */}
      <JamSessionPanelWrapper
        isOpen={showJamSessionPanel}
        activeJamSession={activeJamSession}
        user={user}
        setShowJamSessionPanel={setShowJamSessionPanel}
        setActiveJamSession={setActiveJamSession}
        setCurrentTime={setCurrentTime}
        isPlaying={isPlaying}
        playAll={playAll}
        stopAll={stopAll}
      />
      
      {/* Plugin Rack Panel - Right Side */}
      <PluginRackPanel
        isOpen={showPluginRack}
        onClose={() => setShowPluginRack(false)}
        masterPluginChain={masterPluginChain}
        setShowPluginBrowser={setShowPluginBrowser}
        setShowChainPresets={setShowChainPresets}
        handleRemoveMasterPlugin={handleRemoveMasterPlugin}
        handleToggleMasterPluginBypass={handleToggleMasterPluginBypass}
        handleReorderMasterPlugins={handleReorderMasterPlugins}
        handleMasterPluginParamChange={handleMasterPluginParamChange}
        handleDuplicateMasterPlugin={handleDuplicateMasterPlugin}
        handleResetMasterPlugin={handleResetMasterPlugin}
      />
      
      {/* Plugin Browser Modal - Master */}
      <PluginBrowserWrapper
        isOpen={showPluginBrowser}
        onClose={() => setShowPluginBrowser(false)}
        onAddPlugin={handleAddMasterPlugin}
        onAddToTrack={null}
        targetTrackId={null}
        trackName={null}
      />
      
      {/* Plugin Browser Modal - Track */}
      <PluginBrowserWrapper
        isOpen={trackPluginBrowserTarget !== null}
        onClose={() => setTrackPluginBrowserTarget(null)}
        onAddPlugin={null}
        onAddToTrack={(pluginName) => handleAddTrackPlugin(trackPluginBrowserTarget, pluginName)}
        targetTrackId={trackPluginBrowserTarget}
        trackName={tracks.find(t => t.id === trackPluginBrowserTarget)?.name}
      />
      
      {/* Plugin Marketplace */}
      <PluginMarketplaceWrapper
        isOpen={showPluginMarketplace}
        onClose={() => setShowPluginMarketplace(false)}
        installedPlugins={[]}
      />
      
      {/* Chain Preset Selector Modal */}
      <ChainPresetSelectorWrapper
        isOpen={showChainPresets}
        onClose={() => setShowChainPresets(false)}
        currentChain={masterPluginChain}
        onLoadPreset={handleLoadChainPreset}
        onSavePreset={() => {}}
        userId={user?.id || user?.email}
      />
      
      {/* Track Preset Selector Modal */}
      <TrackPresetSelector
        isOpen={trackPresetTarget !== null}
        onClose={() => setTrackPresetTarget(null)}
        onLoadPreset={handleLoadTrackPreset}
        currentPlugins={tracks.find(t => t.id === trackPresetTarget)?.pluginChain || []}
        trackId={trackPresetTarget}
        trackName={tracks.find(t => t.id === trackPresetTarget)?.name || ''}
        onSavePreset={() => {}}
      />
      
      {/* Batch Track Exporter Modal */}
      <BatchExporterWrapper
        isOpen={showBatchExporter}
        onClose={() => setShowBatchExporter(false)}
        tracks={tracks}
        onImportTracks={handleBatchImportTracks}
        projectName={projectName}
        audioContextRef={audioContextRef}
      />
      
      {/* Session Manager Modal */}
      <SessionManagerWrapper
        isOpen={showSessionManager}
        onClose={() => setShowSessionManager(false)}
        currentSessionId={collabSessionId}
        onCreateSession={handleCreateSession}
        onJoinSession={handleJoinSession}
        onLeaveSession={handleLeaveSession}
        userName={user?.name || user?.email?.split('@')[0] || 'Guest User'}
      />
      
      {/* Jam Session Modal */}
      <JamSessionModalWrapper
        isOpen={showJamSessionModal}
        onClose={() => setShowJamSessionModal(false)}
        onSessionJoined={(session) => {
          setActiveJamSession(session);
          setShowJamSessionModal(false);
          setShowJamSessionPanel(true);
        }}
        userId={user?.id || user?.email}
        userName={user?.name || user?.email?.split('@')[0] || 'Guest User'}
        currentProjectId={projectId}
      />
      
      {/* Keyboard Shortcuts Panel */}
      <KeyboardShortcutsPanelWrapper
        isOpen={showKeyboardShortcuts}
        onClose={() => setShowKeyboardShortcuts(false)}
      />
      
      {/* Quick Tour for New Users */}
      <QuickTour
        isOpen={showTour}
        onClose={closeTour}
        onComplete={completeTour}
      />
      
      {/* Project Template Selector */}
      <ProjectTemplateSelector
        isOpen={showTemplateSelector}
        onClose={() => setShowTemplateSelector(false)}
        onSelectTemplate={handleSelectTemplate}
      />
      
      {/* Markers & Regions Panel (Floating) */}
      <MarkersPanelWrapper
        isOpen={showMarkersPanel}
        markers={markers}
        regions={regions}
        addMarker={addMarker}
        updateMarker={updateMarker}
        deleteMarker={deleteMarker}
        addRegion={addRegion}
        updateRegion={updateRegion}
        deleteRegion={deleteRegion}
        setCurrentTime={setCurrentTime}
        audioContextRef={audioContextRef}
        playbackStartTimeRef={playbackStartTimeRef}
        currentTime={currentTime}
        totalDuration={totalDuration}
      />
      
      {/* Marker Type Picker (Floating) */}
      <MarkerPickerWrapper
        isOpen={showMarkerPicker}
        pendingMarkerTime={pendingMarkerTime}
        markerPickerPosition={markerPickerPosition}
        addMarker={addMarker}
        onClose={() => {
          setShowMarkerPicker(false);
          setPendingMarkerTime(null);
        }}
      />
      
      {/* Bottom Status Bar */}
      <StatusBar
        sampleRate={SAMPLE_RATE}
        trackCount={tracks.length}
        bpm={bpm}
        sessionId={collabSessionId}
        projectStatus={isSaving ? 'saving' : 'ready'}
      />
      
      {/* Advanced Effects Modal */}
      <AdvancedEffectsModal
        isOpen={showAdvancedEffects}
        onClose={() => setShowAdvancedEffects(false)}
        tracks={tracks}
        selectedEffectsTrackId={selectedEffectsTrackId}
        advancedEffectsData={advancedEffectsData}
        setAdvancedEffectsData={setAdvancedEffectsData}
        updateTrack={updateTrack}
        isPro={isPro}
        isStudio={isStudio}
        setShowEffectPresetsBrowser={setShowEffectPresetsBrowser}
      />
      
      {/* Effect Presets Browser */}
      <EffectPresetsBrowserWrapper
        isOpen={showEffectPresetsBrowser}
        onClose={() => setShowEffectPresetsBrowser(false)}
        advancedEffectsData={advancedEffectsData}
        setAdvancedEffectsData={setAdvancedEffectsData}
        selectedEffectsTrackId={selectedEffectsTrackId}
        updateTrack={updateTrack}
        userId={user?.id || user?.email}
      />
      
      {/* AI Mix Assistant */}
      <AIMixAssistantWrapper
        isOpen={showAIMixAssistant}
        onClose={() => setShowAIMixAssistant(false)}
        tracks={tracks}
        projectName={projectName}
        updateTrack={updateTrack}
        setMasterPluginChain={setMasterPluginChain}
      />
      
      {/* AI Chord Generator */}
      <AIChordGeneratorWrapper
        isOpen={showAIChordGenerator}
        onClose={() => setShowAIChordGenerator(false)}
        audioContext={audioContextRef.current}
        tracks={tracks}
        bpm={bpm}
        updateTrack={updateTrack}
        setTracks={setTracks}
        TRACK_COLORS={TRACK_COLORS}
      />
      
      {/* AI Lyric Assistant */}
      <AILyricAssistantWrapper
        isOpen={showAILyricAssistant}
        onClose={() => setShowAILyricAssistant(false)}
      />
      
      {/* AI Drum Pattern Generator */}
      <AIDrumGeneratorWrapper
        isOpen={showAIDrumGenerator}
        onClose={() => setShowAIDrumGenerator(false)}
        audioContext={audioContextRef.current}
        bpm={bpm}
        tracks={tracks}
        updateTrack={updateTrack}
        setTracks={setTracks}
        TRACK_COLORS={TRACK_COLORS}
      />
      
      {/* Video Sync Panel */}
      <VideoSyncPanelWrapper
        isOpen={showVideoSync}
        onClose={() => setShowVideoSync(false)}
        projectId="current-project"
        currentTime={currentTime}
        isPlaying={isPlaying}
        onTimeUpdate={(time) => setCurrentTime(time)}
        onPlayStateChange={(playing) => playing ? playAll() : stopAll()}
        bpm={bpm}
      />
      
      {/* Sample Library Browser */}
      <SampleLibraryWrapper
        isOpen={showSampleLibrary}
        onClose={() => setShowSampleLibrary(false)}
        audioContext={audioContextRef.current}
        tracks={tracks}
        currentTime={currentTime}
        setTracks={setTracks}
        TRACK_COLORS={TRACK_COLORS}
      />
    </>
  );
};

export default DAWModalsContainer;
